import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search,
  Send,
  User,
  Bot,
  ShieldCheck,
  RefreshCw,
  Clock,
  Lock,
  PhoneCall,
  Phone,
  Check,
  CheckCheck,
  AlertCircle,
  Smile,
  Paperclip,
  Mic,
  MoreVertical,
  ArrowLeft,
  X,
  Volume2,
  Play,
  Pause,
  ExternalLink,
  ShoppingBag,
  Sparkles,
  Smartphone,
  Maximize2,
  Minimize2,
  Copy,
  LogOut,
  LayoutDashboard,
  Share2,
  QrCode,
  Bell,
  BellRing,
  BellOff,
  Download,
  FileText,
  Image as ImageIcon,
  Trash2,
} from 'lucide-react';
import { KangavenaWhatsAppMobileModal } from './KangavenaWhatsAppMobileModal';
import { MessageVisualContent } from './MessageVisualContent';

// ── Phone & Message Classifier Helpers ──
export const formatPhoneNumber = (phone?: string) => {
  if (!phone) return '';
  const clean = phone.replace(/\D/g, '');
  if (clean.length === 11 && clean.startsWith('94')) {
    return `+94 ${clean.slice(2, 4)} ${clean.slice(4, 7)} ${clean.slice(7)}`;
  }
  if (clean.length === 10 && clean.startsWith('0')) {
    return `+94 ${clean.slice(1, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`;
  }
  return `+${clean}`;
};

export const getDisplayCustomerName = (s: any): string | null => {
  if (!s) return null;
  const raw = s.customerName ||
    (s.latestOrder?.customerName && typeof s.latestOrder.customerName === 'string' ? s.latestOrder.customerName : null) ||
    (s.latestLead?.visitorName && typeof s.latestLead.visitorName === 'string' ? s.latestLead.visitorName : null) ||
    (s.visitorName && typeof s.visitorName === 'string' ? s.visitorName : null);

  if (!raw || typeof raw !== 'string') return null;
  const stripped = raw.split('(')[0].trim();
  if (stripped.length < 2) return null;
  const lower = stripped.toLowerCase();
  if (
    lower === 'null' ||
    lower === 'undefined' ||
    lower === 'customer' ||
    lower.startsWith('customer') ||
    lower.startsWith('whatsapp') ||
    lower.startsWith('voice caller') ||
    lower.startsWith('caller') ||
    lower.startsWith('visitor') ||
    lower.startsWith('lead') ||
    lower.startsWith('order') ||
    lower.includes('unknown') ||
    /^\+?\d+$/.test(stripped.replace(/[\s\-\(\)]/g, ''))
  ) {
    return null;
  }
  return stripped;
};

export const isCustomerMessage = (m: any) => {
  if (!m) return false;
  const sender = (m.sender || '').toLowerCase();
  const role = (m.role || '').toLowerCase();
  if (
    sender === 'luna' ||
    role === 'luna' ||
    sender === 'assistant' ||
    role === 'assistant' ||
    sender === 'ai' ||
    role === 'model' ||
    sender === 'human_admin' ||
    role === 'human_admin' ||
    sender === 'agent' ||
    role === 'agent' ||
    sender === 'system' ||
    role === 'system'
  ) {
    return false;
  }
  return true;
};

export const isLunaMessage = (m: any) => {
  if (!m) return false;
  const sender = (m.sender || '').toLowerCase();
  const role = (m.role || '').toLowerCase();
  return (
    sender === 'luna' ||
    role === 'luna' ||
    sender === 'assistant' ||
    role === 'assistant' ||
    sender === 'ai' ||
    role === 'model'
  );
};

export const isCustomerRequestingHuman = (text?: string) => {
  if (!text) return false;
  return /(human|agent|person|manager|owner|call|support|talk to (a )?human|speak to (a )?human|real person|help me|manushyaye|kenek ekka katha|katha karanna oni|admin|representative|help)/i.test(
    text
  );
};

export interface LiveChatMessage {
  sender: string;
  role?: string;
  text: string;
  time: string;
  timestamp?: string;
  status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | string;
  mediaUrl?: string | null;
  mediaUrls?: string[];
  dispatchedMedia?: string[];
  mediaType?: string | null;
  mediaCaption?: string | null;
  isAudio?: boolean;
  audioUrl?: string | null;
  isVoiceTurn?: boolean;
  duration?: number | string | null;
  played?: boolean;
  clientMsgId?: string | null;
  nodeUsed?: string;
  toolsUsed?: string[];
}

export interface LiveChatSession {
  sessionId: string;
  visitorName: string;
  customerName?: string | null;
  pageUrl?: string;
  status: string;
  startedAt: string;
  lastActiveAt?: string;
  phone?: string;
  channel?: string;
  isVoiceCall?: boolean;
  callDuration?: number;
  callStatus?: string;
  isHumanTakeover?: boolean;
  takeoverAt?: string | null;
  takeoverExpiresAt?: string | null;
  takeoverMode?: 'timed_30m' | 'permanent';
  humanRequested?: boolean;
  hasOrder?: boolean;
  orderCount?: number;
  latestOrder?: {
    orderNumber?: string;
    totalAmount?: number | string;
    currency?: string;
    status?: string;
    itemsSummary?: string;
    createdAt?: string;
  } | null;
  hasLead?: boolean;
  leadCount?: number;
  latestLead?: {
    formType?: string;
    status?: string;
    notes?: string;
    createdAt?: string;
  } | null;
  leadOrOrderCaptured?: boolean;
  instancePhoneNumber?: string | null;
  instancePhoneNumberId?: string | null;
  instanceLabel?: string | null;
  isPersonalInstance?: boolean;
  messages: LiveChatMessage[];
}

// ── Authentic WhatsApp Status Receipt Component ──
export const WhatsAppReceipt: React.FC<{
  status?: string;
  className?: string;
}> = ({ status = 'delivered', className = '' }) => {
  if (status === 'pending') {
    return <Clock size={11} className={`text-[#8696a0] shrink-0 animate-pulse ${className}`} title="Sending to WhatsApp..." />;
  }
  if (status === 'failed') {
    return <AlertCircle size={12} className={`text-rose-400 shrink-0 ${className}`} title="Delivery Failed" />;
  }
  if (status === 'sent') {
    return <Check size={14} className={`text-[#8696a0] shrink-0 ${className}`} title="Sent to server" />;
  }
  if (status === 'delivered') {
    return <CheckCheck size={14} className={`text-[#8696a0] shrink-0 ${className}`} title="Delivered to WhatsApp recipient" />;
  }
  // 'read' double cyan checkmarks
  return <CheckCheck size={14} className={`text-[#53bdeb] shrink-0 ${className}`} title="Read by recipient" />;
};

// ── Waveform Heights for Authentic WhatsApp PTT Notes ──
const VOICE_WAVE_BARS = [14, 22, 12, 28, 18, 10, 24, 16, 26, 20, 12, 24, 18, 28, 14, 22, 16, 26, 12, 20, 24, 16, 28, 18, 12, 22, 16, 24];

// ── Authentic WhatsApp Voice Note Player Component ──
export const WhatsAppVoiceNotePlayer: React.FC<{
  audioUrl?: string | null;
  text?: string;
  duration?: number | string | null;
  played?: boolean;
  isIncoming: boolean;
  time: string;
  status?: string;
  token?: string;
  sessionId?: string;
  messageIndex?: number;
  onMarkPlayed?: () => void;
}> = ({
  audioUrl,
  text,
  duration,
  played = false,
  isIncoming,
  time,
  status = 'delivered',
  token,
  sessionId,
  messageIndex,
  onMarkPlayed,
}) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPlayed, setIsPlayed] = useState<boolean>(Boolean(played));
  const [progress, setProgress] = useState<number>(0);
  const [currentTimeSec, setCurrentTimeSec] = useState<number>(0);
  const [realDuration, setRealDuration] = useState<number | null>(null);
  const [speed, setSpeed] = useState<1 | 1.5 | 2>(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const simTimerRef = useRef<any>(null);

  const totalDurationSec = useMemo(() => {
    if (typeof realDuration === 'number' && realDuration > 0) return Math.ceil(realDuration);
    if (typeof duration === 'number' && duration > 0) return duration;
    if (typeof duration === 'string') {
      const parsed = parseInt(duration, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return 14;
  }, [realDuration, duration]);

  // Reactively initialize audio element when audioUrl is present or changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setProgress(0);
    setCurrentTimeSec(0);

    if (audioUrl) {
      const formattedUrl = audioUrl.startsWith('http') || audioUrl.startsWith('blob:')
        ? audioUrl
        : (audioUrl.startsWith('/') ? audioUrl : `/${audioUrl}`);
      try {
        const audio = new Audio(formattedUrl);
        audio.preload = 'metadata';
        audio.playbackRate = speed;
        audio.onloadedmetadata = () => {
          if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
            setRealDuration(audio.duration);
          }
        };
        audio.onended = () => {
          setIsPlaying(false);
          setProgress(1);
          setCurrentTimeSec(Math.ceil(audio.duration || totalDurationSec));
        };
        audio.ontimeupdate = () => {
          const cur = audio.currentTime;
          const dur = audio.duration || totalDurationSec;
          setCurrentTimeSec(Math.floor(cur));
          setProgress(dur > 0 ? Math.min(1, cur / dur) : 0);
        };
        audio.onerror = (e) => {
          console.warn('[VoicePlayer] HTML5 audio error on url:', formattedUrl, e);
        };
        audioRef.current = audio;
      } catch (audioInitErr) {
        console.warn('[VoicePlayer] Could not construct Audio object for:', formattedUrl, audioInitErr);
      }
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      clearInterval(simTimerRef.current);
    };
  }, [audioUrl]);

  const markAsPlayed = () => {
    if (!isPlayed) {
      setIsPlayed(true);
      if (onMarkPlayed) onMarkPlayed();
      if (token && sessionId !== undefined && messageIndex !== undefined) {
        fetch('/api/tenet/live-chats/mark-read', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ sessionId, messageIndex, isVoicePlayed: true }),
        }).catch(() => {});
      }
    }
  };

  const handleTogglePlay = () => {
    markAsPlayed();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        if (progress >= 1) {
          audioRef.current.currentTime = 0;
          setProgress(0);
          setCurrentTimeSec(0);
        }
        audioRef.current.playbackRate = speed;
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch((e) => {
            console.warn('[VoicePlayer] Audio play failed, falling back to simulated playback:', e);
            startSimulatedPlayback();
          });
      }
    } else {
      if (isPlaying) {
        stopSimulatedPlayback();
      } else {
        startSimulatedPlayback();
      }
    }
  };

  const startSimulatedPlayback = () => {
    setIsPlaying(true);
    let cur = progress >= 1 ? 0 : currentTimeSec;
    clearInterval(simTimerRef.current);
    simTimerRef.current = setInterval(() => {
      cur += 0.25 * speed;
      if (cur >= totalDurationSec) {
        clearInterval(simTimerRef.current);
        setIsPlaying(false);
        setProgress(1);
        setCurrentTimeSec(totalDurationSec);
      } else {
        setCurrentTimeSec(Math.floor(cur));
        setProgress(cur / totalDurationSec);
      }
    }, 250);
  };

  const stopSimulatedPlayback = () => {
    clearInterval(simTimerRef.current);
    setIsPlaying(false);
  };

  const handleChangeSpeed = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextSpeed: 1 | 1.5 | 2 = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  const handleSeek = (idx: number) => {
    const pct = idx / (VOICE_WAVE_BARS.length - 1);
    setProgress(pct);
    const targetSec = pct * totalDurationSec;
    setCurrentTimeSec(Math.floor(targetSec));
    if (audioRef.current) {
      audioRef.current.currentTime = pct * (audioRef.current.duration || totalDurationSec);
    }
  };

  const formatSec = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col min-w-[260px] sm:min-w-[300px] max-w-full">
      {/* Audio Control Row */}
      <div className="flex items-center gap-3 py-1">
        <button
          type="button"
          onClick={handleTogglePlay}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition shrink-0 cursor-pointer shadow-md ${
            isIncoming
              ? 'bg-[#00a884] hover:bg-[#00a884]/90 text-white'
              : 'bg-[#111b21] hover:bg-[#202c33] text-[#00a884] border border-[#00a884]/40'
          }`}
          title={isPlaying ? 'Pause' : 'Play Voice Note'}
        >
          {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
        </button>

        {/* Waveform Bars */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-[2.5px] h-7 cursor-pointer py-1" title="Click to seek">
            {VOICE_WAVE_BARS.map((height, idx) => {
              const barPct = idx / (VOICE_WAVE_BARS.length - 1);
              const isPast = barPct <= progress;
              return (
                <div
                  key={idx}
                  onClick={() => handleSeek(idx)}
                  style={{ height: `${height}px` }}
                  className={`flex-1 rounded-full transition-colors duration-150 ${
                    isPast
                      ? 'bg-[#53bdeb]'
                      : 'bg-[#8696a0]/50 hover:bg-[#8696a0]'
                  }`}
                />
              );
            })}
          </div>

          <div className="flex items-center justify-between text-[11px] font-mono text-[#8696a0] mt-0.5">
            <span>{isPlaying ? formatSec(currentTimeSec) : formatSec(totalDurationSec)}</span>

            <button
              type="button"
              onClick={handleChangeSpeed}
              className="px-1.5 py-0.5 rounded-full bg-white/10 hover:bg-white/20 text-[9px] font-bold text-[#e9edef] font-mono transition cursor-pointer"
              title="Change speed"
            >
              {speed}x
            </button>
          </div>
        </div>

        {/* Authentic Mic Avatar */}
        <div className="relative shrink-0 ml-1">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center transition border ${
              isPlayed
                ? 'bg-[#53bdeb]/15 border-[#53bdeb]/50 text-[#53bdeb]'
                : 'bg-[#202c33] border-[#2a3942] text-[#8696a0]'
            }`}
            title={isPlayed ? 'Voice played' : 'Voice unplayed'}
          >
            <Mic size={16} className={isPlayed ? 'text-[#53bdeb]' : 'text-[#8696a0]'} />
          </div>
        </div>
      </div>

      {/* Transcription snippet if available */}
      {text && (
        <div className="mt-2 pt-2 border-t border-white/10 text-xs text-[#d1d7db] leading-relaxed">
          <div className="flex items-center gap-1 text-[10px] text-[#00a884] font-bold uppercase tracking-wider mb-0.5">
            <Sparkles size={10} />
            <span>Transcription</span>
          </div>
          <p className="select-text whitespace-pre-wrap">{text}</p>
        </div>
      )}

      {/* Bottom Receipt & Time */}
      <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-[#8696a0] font-mono float-right ml-auto">
        <span>{time}</span>
        {!isIncoming && (
          <div className="flex items-center gap-0.5 ml-1">
            <Mic size={12} className={isPlayed ? 'text-[#53bdeb]' : 'text-[#8696a0]'} title={isPlayed ? 'Voice Note Played' : 'Voice Note Delivered'} />
            <WhatsAppReceipt status={isPlayed ? 'read' : status} />
          </div>
        )}
      </div>
    </div>
  );
};

interface KangavenaWhatsAppWebProps {
  token: string;
  tenant?: any;
  onClose?: () => void;
  initialSessionId?: string | null;
  standalone?: boolean;
  onLogout?: () => void;
}

export const KangavenaWhatsAppWeb: React.FC<KangavenaWhatsAppWebProps> = ({
  token,
  tenant,
  onClose,
  initialSessionId = null,
  standalone = false,
  onLogout,
}) => {
  const [sessions, setSessions] = useState<LiveChatSession[]>([]);
  const [connectedInstances, setConnectedInstances] = useState<any[]>([]);
  const [selectedAccountFilter, setSelectedAccountFilter] = useState<string>('all');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(initialSessionId);
  const [replyMessage, setReplyMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterTab, setFilterTab] = useState<'all' | 'orders' | 'leads' | 'unread' | 'voice' | 'takeover'>('all');
  const [orderCycleFilter, setOrderCycleFilter] = useState<'all' | 'current_month'>('all');
  const [orderMetrics, setOrderMetrics] = useState<{
    totalOrdersCount: number;
    totalOrdersAmount: number;
    currentMonthOrdersCount: number;
    currentMonthOrdersAmount: number;
    currentMonthKey?: string;
    monthlyOrderStats?: Record<string, { count: number; amount: number; monthName: string }>;
  } | null>(null);
  const [serverTotalChatsCount, setServerTotalChatsCount] = useState<number | null>(null);

  const accountFilteredSessions = useMemo(() => {
    if (selectedAccountFilter === 'all') return sessions;
    const targetClean = selectedAccountFilter.replace(/\D/g, '');
    const isTargetPhone = targetClean.length >= 8;
    return sessions.filter((s) => {
      const matchNodeId = (s as any).instanceNodeId && (s as any).instanceNodeId === selectedAccountFilter;
      const matchInstId = s.instancePhoneNumberId && s.instancePhoneNumberId === selectedAccountFilter;
      const matchInstPhone = isTargetPhone && s.instancePhoneNumber && s.instancePhoneNumber.replace(/\D/g, '').includes(targetClean);
      const matchSessionId = isTargetPhone && s.sessionId && s.sessionId.includes(targetClean);
      return matchNodeId || matchInstId || matchInstPhone || matchSessionId;
    });
  }, [sessions, selectedAccountFilter]);

  const ordersCount = useMemo(() => accountFilteredSessions.filter((s) => s.hasOrder).length, [accountFilteredSessions]);
  const displayOrdersCount = orderMetrics?.totalOrdersCount ? Math.max(ordersCount, orderMetrics.totalOrdersCount) : ordersCount;
  const currentMonthOrdersCount = orderMetrics?.currentMonthOrdersCount ?? displayOrdersCount;
  const leadsCount = useMemo(() => accountFilteredSessions.filter((s) => s.hasLead).length, [accountFilteredSessions]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [toggling, setToggling] = useState<boolean>(false);
  const [showAttachMenu, setShowAttachMenu] = useState<boolean>(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [takeoverCountdown, setTakeoverCountdown] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);
  const [showMobileModal, setShowMobileModal] = useState<boolean>(false);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<{ sessionId: string; name: string; phone?: string } | null>(null);
  const [showClearAllModal, setShowClearAllModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteToast, setDeleteToast] = useState<string | null>(null);

  // ── Cross-Platform Alert & Notification Engine ──
  const [alertsEnabled, setAlertsEnabled] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kangavena_wa_alerts') !== 'false';
    }
    return true;
  });
  const alertsEnabledRef = useRef<boolean>(alertsEnabled);
  useEffect(() => {
    alertsEnabledRef.current = alertsEnabled;
  }, [alertsEnabled]);

  const [incomingAlert, setIncomingAlert] = useState<{
    phone: string;
    text: string;
    sessionId: string;
    isTakeover?: boolean;
    isHumanRequested?: boolean;
  } | null>(null);

  const initialAutoSelectDoneRef = useRef<boolean>(false);
  const isFirstFetchRef = useRef<boolean>(true);
  const knownMessageIdsRef = useRef<Set<string>>(new Set());
  const audioCtxRef = useRef<AudioContext | null>(null);

  const initOrResumeAudio = () => {
    try {
      if (!audioCtxRef.current) {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          audioCtxRef.current = new AudioCtx();
        }
      }
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
    } catch (e) {
      console.warn('[WhatsAppWeb] Audio unlock error:', e);
    }
  };

  // ── Authentic WhatsApp Web Audio Chime (Zero External Dependencies) ──
  const playWhatsAppChime = () => {
    try {
      initOrResumeAudio();
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const now = ctx.currentTime;

      // Note 1: 880Hz (A5)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now);
      gain1.gain.setValueAtTime(0.3, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.16);

      // Note 2: 1320Hz (E6) high harmonic crystal resolve
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(1320, now + 0.08);
      gain2.gain.setValueAtTime(0.35, now + 0.08);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.08);
      osc2.stop(now + 0.35);
    } catch (e) {
      console.warn('[WhatsAppWeb] Audio chime failed:', e);
    }
  };

  // ── PWA Installation Support (Add to Home Screen) ──
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  });

  useEffect(() => {
    const handleBeforeInstall = (e: any) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setDeferredInstallPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallApp = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const choice = await deferredInstallPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setIsAppInstalled(true);
      }
      setDeferredInstallPrompt(null);
    } else {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      if (isIOS) {
        alert('To install on iPhone/iPad:\n1. Tap the Share icon at the bottom of Safari\n2. Tap "Add to Home Screen" 📲');
      } else {
        alert('To install as an app:\nOpen browser menu (⋮) and select "Install app" or "Add to Home screen" 📲');
      }
    }
  };

  // ── Native Browser & ServiceWorker Rich Notification (Android Chrome, Apple iOS PWA, macOS, Windows) ──
  const showBrowserNotification = async (
    title: string,
    body: string,
    sessionId?: string,
    options?: { renotify?: boolean; vibrate?: number[] }
  ) => {
    try {
      if (typeof window === 'undefined') return;

      const renotify = options?.renotify ?? true;
      const vibrate = options?.vibrate || [200, 100, 200];

      // 1. Prioritize Service Worker registration for rich Android Chrome & iOS PWA preview
      if ('serviceWorker' in navigator) {
        try {
          let reg: ServiceWorkerRegistration | undefined;
          try {
            reg = await navigator.serviceWorker.ready;
          } catch {}
          if (!reg) {
            reg = await navigator.serviceWorker.getRegistration();
          }
          if (!reg && navigator.serviceWorker.register) {
            reg = await navigator.serviceWorker.register('/sw.js');
          }

          if (reg && reg.showNotification) {
            await reg.showNotification(title, {
              body: body || 'New incoming message',
              icon: '/meta-app-icon-1024.png',
              badge: '/meta-app-icon-1024.png',
              tag: `wa-msg-${sessionId || 'default'}`,
              vibrate,
              renotify,
              data: { sessionId },
            } as any);
            return;
          }
        } catch (swErr) {
          console.warn('[WhatsAppWeb] SW rich notification fallback:', swErr);
        }
      }

      // 2. Standard window Notification API fallback (Desktop browsers)
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const notif = new Notification(title, {
            body: body || 'New incoming message',
            icon: '/meta-app-icon-1024.png',
            tag: `wa-msg-${sessionId || 'default'}`,
          });
          if (sessionId) {
            notif.onclick = () => {
              window.focus();
              setSelectedSessionId(sessionId);
              notif.close();
            };
          }
        } catch (winNotifErr) {
          console.warn('[WhatsAppWeb] window.Notification error:', winNotifErr);
        }
      }
    } catch (e) {
      console.warn('[WhatsAppWeb] Browser notification error:', e);
    }
  };

  const handleToggleAlerts = async () => {
    const next = !alertsEnabled;
    setAlertsEnabled(next);
    localStorage.setItem('kangavena_wa_alerts', next ? 'true' : 'false');

    if (next) {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
          try {
            await Notification.requestPermission();
          } catch {}
        }
      }
      initOrResumeAudio();
      playWhatsAppChime();
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          navigator.vibrate([100, 50, 100]);
        } catch {}
      }
    }
  };

  // ── Handlers for Deleting Single Chat & Clearing All WhatsApp Chats ──
  const handleDeleteChat = async (sessionId: string) => {
    if (!sessionId || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`/api/tenet/live-chats/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
        if (selectedSessionId === sessionId) {
          setSelectedSessionId(null);
        }
        setDeleteToast('✅ Chat deleted successfully.');
        setTimeout(() => setDeleteToast(null), 3500);
      } else {
        alert(data.error || 'Failed to delete conversation');
      }
    } catch (err: any) {
      console.error('[WhatsAppWeb] Error deleting chat:', err);
      alert('Network error deleting chat');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmTarget(null);
    }
  };

  const handleClearAllChats = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await fetch('/api/tenet/live-chats/delete-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ channel: 'whatsapp' })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setSessions([]);
        setSelectedSessionId(null);
        setDeleteToast(`✅ All WhatsApp chats cleared (${data.deletedCount || 0} deleted). Luna AI context reset!`);
        setTimeout(() => setDeleteToast(null), 4000);
      } else {
        alert(data.error || 'Failed to clear chats');
      }
    } catch (err: any) {
      console.error('[WhatsAppWeb] Error clearing all chats:', err);
      alert('Network error clearing chats');
    } finally {
      setIsDeleting(false);
      setShowClearAllModal(false);
    }
  };

  // Unlock AudioContext and check notification permission on initial user touch/click for mobile browsers (iOS / Android)
  useEffect(() => {
    const unlockAudio = () => {
      initOrResumeAudio();
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    };
    window.addEventListener('click', unlockAudio, { once: true, passive: true });
    window.addEventListener('touchstart', unlockAudio, { once: true, passive: true });
    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  // Listen for Service Worker notification clicks to automatically focus and select chat
  useEffect(() => {
    const handleSwMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SELECT_WHATSAPP_SESSION' && event.data?.sessionId) {
        setSelectedSessionId(event.data.sessionId);
      }
    };
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
    }
    return () => {
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSwMessage);
      }
    };
  }, []);

  // Auto-dismiss in-app notification banner after 7 seconds
  useEffect(() => {
    if (incomingAlert) {
      const timer = setTimeout(() => {
        setIncomingAlert(null);
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [incomingAlert]);

  const handleCopyMobileLink = () => {
    const url = `${window.location.origin}/whatsapp`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const chatScrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingLocalMessagesRef = useRef<Map<string, LiveChatMessage[]>>(new Map());

  // ── Fetch Live WhatsApp & Visitor Conversations ──
  const fetchChats = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/tenet/live-chats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.connectedInstances)) {
        setConnectedInstances(data.connectedInstances);
      }
      if (data.orderMetrics) {
        setOrderMetrics(data.orderMetrics);
      }
      if (typeof data.totalChatsCount === 'number') {
        setServerTotalChatsCount(data.totalChatsCount);
      }
      if (data.success && Array.isArray(data.activeChats)) {
        // Merge with pending local messages so outgoing messages NEVER vanish during polling
        const mergedChats = data.activeChats.map((remoteChat: LiveChatSession) => {
          const pending = pendingLocalMessagesRef.current.get(remoteChat.sessionId) || [];
          if (!pending.length) return remoteChat;

          const remoteClientIds = new Set(
            (remoteChat.messages || []).map((m) => m.clientMsgId).filter(Boolean)
          );
          const remoteTexts = new Set(
            (remoteChat.messages || []).map((m) => `${m.sender}_${(m.text || '').trim()}`)
          );

          const uncommitted = pending.filter(
            (p) => !remoteClientIds.has(p.clientMsgId) && !remoteTexts.has(`${p.sender}_${(p.text || '').trim()}`)
          );
          // Retain uncommitted messages in pendingLocalMessagesRef until server acknowledges them
          pendingLocalMessagesRef.current.set(remoteChat.sessionId, uncommitted);

          if (uncommitted.length > 0) {
            return {
              ...remoteChat,
              messages: [...remoteChat.messages, ...uncommitted],
            };
          }
          return remoteChat;
        });

        // Resilient phone-level deduplication: if multiple sessions exist for the same customer phone, merge them
        const phoneSessionMap = new Map<string, LiveChatSession>();
        const deduplicatedChats: LiveChatSession[] = [];
        for (const chat of mergedChats) {
          const cleanP = chat.phone ? String(chat.phone).replace(/\D/g, '') : '';
          const key = cleanP && cleanP.length >= 8 ? cleanP.slice(-9) : chat.sessionId;
          if (!phoneSessionMap.has(key)) {
            phoneSessionMap.set(key, chat);
            deduplicatedChats.push(chat);
          } else {
            const existing = phoneSessionMap.get(key)!;
            // Prefer the session with more messages, or merge
            if ((chat.messages || []).length > (existing.messages || []).length) {
              const updated = {
                ...existing,
                ...chat,
                hasLead: existing.hasLead || chat.hasLead,
                latestLead: existing.latestLead || chat.latestLead,
                hasOrder: existing.hasOrder || chat.hasOrder,
                latestOrder: existing.latestOrder || chat.latestOrder
              };
              phoneSessionMap.set(key, updated);
              const idx = deduplicatedChats.findIndex(c => c.sessionId === existing.sessionId);
              if (idx !== -1) deduplicatedChats[idx] = updated;
            }
          }
        }

        setSessions(deduplicatedChats);

        // ── Auto-Select Logic: Desktop Only on Initial Mount ──
        // On mobile (<768px), always remain on the conversation list so users can pick a chat or return without being trapped!
        if (!initialAutoSelectDoneRef.current && mergedChats.length > 0) {
          initialAutoSelectDoneRef.current = true;
          if (typeof window !== 'undefined' && window.innerWidth >= 768 && !selectedSessionId) {
            setSelectedSessionId(mergedChats[0].sessionId);
          }
        }

        // ── Cross-Platform Customer Message Alert Detection ──
        if (isFirstFetchRef.current) {
          isFirstFetchRef.current = false;
          mergedChats.forEach((chat) => {
            chat.messages?.forEach((m) => {
              const msgKey = m.clientMsgId || `${chat.sessionId}_${m.time}_${(m.text || '').slice(0, 40)}`;
              knownMessageIdsRef.current.add(msgKey);
            });
          });
        } else {
          mergedChats.forEach((chat) => {
            const messages = chat.messages || [];
            const customerMessages = messages.filter(isCustomerMessage);
            const lastCustomerMsg = customerMessages[customerMessages.length - 1];

            const isTakeover = chat.isHumanTakeover || chat.status === 'human_takeover';
            const isHumanRequested =
              chat.humanRequested ||
              chat.status === 'human_requested' ||
              isCustomerRequestingHuman(lastCustomerMsg?.text);

            const displayPhone = chat.phone ? formatPhoneNumber(chat.phone) : chat.visitorName || 'Customer';
            const agentName = tenant?.agentName || 'Luna AI';

            // Find all newly arrived messages in this chat turn
            const newMessages: any[] = [];
            messages.forEach((m) => {
              const msgKey = m.clientMsgId || `${chat.sessionId}_${m.time}_${(m.text || '').slice(0, 40)}`;
              if (!knownMessageIdsRef.current.has(msgKey)) {
                knownMessageIdsRef.current.add(msgKey);
                newMessages.push(m);
              }
            });

            if (newMessages.length > 0) {
              const newCustomerMsgs = newMessages.filter(isCustomerMessage);
              const newLunaMsgs = newMessages.filter(isLunaMessage);

              // We alert whenever a new customer message arrives or Luna AI replies
              if (newCustomerMsgs.length > 0 || newLunaMsgs.length > 0) {
                const effectiveCustomerMsg = newCustomerMsgs[newCustomerMsgs.length - 1] || lastCustomerMsg;
                const custText =
                  effectiveCustomerMsg?.text ||
                  (effectiveCustomerMsg?.isAudio ? '🎤 Voice message' : 'Customer message');

                const hasLunaReplied =
                  newLunaMsgs.length > 0 ||
                  (messages.length > 0 && isLunaMessage(messages[messages.length - 1]));

                if (alertsEnabledRef.current) {
                  playWhatsAppChime();
                  if (typeof navigator !== 'undefined' && navigator.vibrate) {
                    try {
                      navigator.vibrate(
                        isHumanRequested || isTakeover
                          ? [200, 100, 200, 100, 300]
                          : [200, 100, 200]
                      );
                    } catch {}
                  }

                  if (isHumanRequested) {
                    showBrowserNotification(
                      `🚨 ${displayPhone} • Need Attention`,
                      `"${custText}"\n🚨 Need your attention (Customer Requested Human Agent)`,
                      chat.sessionId,
                      { renotify: true, vibrate: [200, 100, 200, 100, 300] }
                    );
                  } else if (isTakeover) {
                    showBrowserNotification(
                      `⚠️ ${displayPhone} • Need Attention`,
                      `"${custText}"\n⚠️ Need your attention (Human Takeover Active)`,
                      chat.sessionId,
                      { renotify: true, vibrate: [200, 100, 200, 100, 300] }
                    );
                  } else if (hasLunaReplied) {
                    showBrowserNotification(
                      `💬 ${displayPhone}`,
                      `"${custText}"\n🤖 Replied by ${agentName}`,
                      chat.sessionId,
                      { renotify: true, vibrate: [200, 100, 200] }
                    );
                  } else {
                    showBrowserNotification(
                      `💬 ${displayPhone}`,
                      `"${custText}"\n⏳ ${agentName} replying...`,
                      chat.sessionId,
                      { renotify: true, vibrate: [200, 100, 200] }
                    );
                  }
                }

                setIncomingAlert({
                  phone: displayPhone,
                  text:
                    hasLunaReplied && !isTakeover && !isHumanRequested
                      ? `"${custText}" • Replied by ${agentName}`
                      : custText,
                  sessionId: chat.sessionId,
                  isTakeover,
                  isHumanRequested,
                });
              }
            }
          });
        }
      }
    } catch (err) {
      console.error('[KangavenaWhatsAppWeb] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChats();
    const interval = setInterval(fetchChats, 5000);
    return () => clearInterval(interval);
  }, []);

  // Update initial selected session if prop changes
  useEffect(() => {
    if (initialSessionId) {
      setSelectedSessionId(initialSessionId);
    }
  }, [initialSessionId]);

  const activeSession = useMemo(() => {
    return sessions.find((s) => s.sessionId === selectedSessionId) || null;
  }, [sessions, selectedSessionId]);

  // ── Auto-scroll to latest message ──
  useEffect(() => {
    if (activeSession?.sessionId && activeSession.messages?.length) {
      const timer = setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [activeSession?.sessionId, activeSession?.messages?.length]);

  // ── Takeover Countdown Timer ──
  useEffect(() => {
    if (!activeSession || (!activeSession.isHumanTakeover && activeSession.status !== 'human_takeover')) {
      setTakeoverCountdown(null);
      return;
    }

    const updateTimer = () => {
      if (activeSession.takeoverMode === 'permanent') {
        setTakeoverCountdown(null);
        return;
      }

      let expiresMs = 0;
      if (activeSession.takeoverExpiresAt) {
        expiresMs = new Date(activeSession.takeoverExpiresAt).getTime();
      } else {
        expiresMs = Date.now() + 30 * 60 * 1000;
      }

      const diffSec = Math.max(0, Math.floor((expiresMs - Date.now()) / 1000));
      setTakeoverCountdown(diffSec);

      if (diffSec === 0) {
        setSessions((prev) =>
          prev.map((s) =>
            s.sessionId === activeSession.sessionId
              ? { ...s, status: 'active_ai', isHumanTakeover: false, takeoverExpiresAt: null }
              : s
          )
        );
      }
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [activeSession?.sessionId, activeSession?.isHumanTakeover, activeSession?.takeoverExpiresAt, activeSession?.takeoverMode]);

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // ── Toggle Human Takeover ──
  const handleToggleTakeover = async (mode: 'timed_30m' | 'permanent' = 'timed_30m', isTakeover: boolean = true) => {
    if (!selectedSessionId) return;
    setToggling(true);
    const expiresAt = isTakeover && mode === 'timed_30m' ? new Date(Date.now() + 30 * 60 * 1000).toISOString() : null;

    try {
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === selectedSessionId
            ? {
                ...s,
                status: isTakeover ? 'human_takeover' : 'active_ai',
                isHumanTakeover: isTakeover,
                takeoverMode: mode,
                takeoverExpiresAt: expiresAt,
              }
            : s
        )
      );

      await fetch('/api/tenet/live-chats/toggle-takeover', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId: selectedSessionId,
          isHumanTakeover: isTakeover,
          takeoverMode: mode,
          durationMinutes: 30,
        }),
      });
    } catch (err) {
      console.error('[KangavenaWhatsAppWeb] Toggle takeover failed:', err);
    } finally {
      setToggling(false);
    }
  };

  // ── Send Human Reply (Auto-activates Takeover) ──
  const handleSendReply = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedSessionId || !replyMessage.trim()) return;

    const messageText = replyMessage.trim();
    setSending(true);
    const effectiveMode = activeSession?.takeoverMode || 'timed_30m';
    const expiresAt = effectiveMode === 'timed_30m' ? new Date(Date.now() + 30 * 60 * 1000).toISOString() : null;
    const clientMsgId = `cmsg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

    // Optimistic UI Append with WhatsApp pending clock icon
    const newLocalMsg: LiveChatMessage = {
      sender: 'human_admin',
      role: 'user',
      text: messageText,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp: new Date().toISOString(),
      status: 'pending',
      clientMsgId,
    };

    // Store in ref to guarantee anti-disappearance across background polls
    const existingPending = pendingLocalMessagesRef.current.get(selectedSessionId) || [];
    pendingLocalMessagesRef.current.set(selectedSessionId, [...existingPending, newLocalMsg]);

    setSessions((prev) =>
      prev.map((s) => {
        if (s.sessionId === selectedSessionId) {
          return {
            ...s,
            status: 'human_takeover',
            isHumanTakeover: true,
            takeoverMode: effectiveMode,
            takeoverExpiresAt: expiresAt,
            messages: [...s.messages, newLocalMsg],
          };
        }
        return s;
      })
    );
    setReplyMessage('');

    try {
      const res = await fetch('/api/tenet/live-chats/reply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId: selectedSessionId,
          phone: activeSession?.phone,
          phoneNumberId: activeSession?.instancePhoneNumberId,
          instancePhoneNumberId: activeSession?.instancePhoneNumberId,
          instancePhoneNumber: activeSession?.instancePhoneNumber,
          instanceNodeId: (activeSession as any)?.instanceNodeId,
          message: messageText,
          takeoverMode: effectiveMode,
          durationMinutes: 30,
          clientMsgId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        // Upgrade optimistic message to delivered status
        setSessions((prev) =>
          prev.map((s) => {
            if (s.sessionId === selectedSessionId) {
              return {
                ...s,
                messages: s.messages.map((m) =>
                  m.clientMsgId === clientMsgId ? { ...m, status: 'delivered' } : m
                ),
              };
            }
            return s;
          })
        );
        // Optimistic status updated to delivered; pendingLocalMessagesRef will automatically clear once confirmed by fetchChats
      } else {
        // Mark status as failed with error icon
        setSessions((prev) =>
          prev.map((s) => {
            if (s.sessionId === selectedSessionId) {
              return {
                ...s,
                messages: s.messages.map((m) =>
                  m.clientMsgId === clientMsgId ? { ...m, status: 'failed' } : m
                ),
              };
            }
            return s;
          })
        );
      }
    } catch (err) {
      console.error('[KangavenaWhatsAppWeb] Send reply error:', err);
      setSessions((prev) =>
        prev.map((s) => {
          if (s.sessionId === selectedSessionId) {
            return {
              ...s,
              messages: s.messages.map((m) =>
                m.clientMsgId === clientMsgId ? { ...m, status: 'failed' } : m
              ),
            };
          }
          return s;
        })
      );
    } finally {
      setSending(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  // ── Filtered Sessions ──
  const filteredSessions = useMemo(() => {
    return accountFilteredSessions.filter((s) => {
      // Tab filter
      if (filterTab === 'orders') {
        if (!s.hasOrder) return false;
        if (orderCycleFilter === 'current_month') {
          const orderDate = (s as any).latestOrder?.createdAt ? new Date((s as any).latestOrder.createdAt) : (s.startedAt ? new Date(s.startedAt) : null);
          if (orderDate && !isNaN(orderDate.getTime())) {
            const now = new Date();
            if (orderDate.getFullYear() !== now.getFullYear() || orderDate.getMonth() !== now.getMonth()) {
              return false;
            }
          }
        }
      }
      if (filterTab === 'leads' && !s.hasLead) {
        return false;
      }
      if (filterTab === 'voice' && !s.isVoiceCall && s.channel !== 'sip_voice' && !s.sessionId.startsWith('call_')) {
        return false;
      }
      if (filterTab === 'takeover' && !s.isHumanTakeover && s.status !== 'human_takeover') {
        return false;
      }
      if (filterTab === 'unread' && s.messages.length === 0) {
        return false;
      }

      // Search filter
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const matchPhone = (s.phone || '').toLowerCase().includes(q);
      const matchName = (getDisplayCustomerName(s) || s.visitorName || '').toLowerCase().includes(q);
      const matchMsg = s.messages.some((m) => (m.text || '').toLowerCase().includes(q));
      return matchPhone || matchName || matchMsg;
    });
  }, [accountFilteredSessions, filterTab, orderCycleFilter, searchQuery]);

  // Quick Emoji Click
  const handleAddEmoji = (emoji: string) => {
    setReplyMessage((prev) => prev + emoji);
    setShowEmojiPicker(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const isTakeoverActive = activeSession?.status === 'human_takeover' || !!activeSession?.isHumanTakeover;

  return (
    <div
      className={`flex flex-col bg-[#0c1317] text-[#e9edef] font-sans antialiased overflow-hidden select-none relative ${
        isFullscreen || standalone
          ? 'fixed inset-0 z-[9999] w-screen h-[100dvh] rounded-none border-none'
          : 'w-full h-full min-h-[680px] max-h-[92vh] rounded-2xl border border-[#222d34] shadow-2xl'
      }`}
    >
      {/* ── In-App Real-Time Customer Message Notification Banner ── */}
      {incomingAlert && (
        <div
          className={`absolute top-3 left-1/2 -translate-x-1/2 z-50 max-w-md w-[94%] backdrop-blur-md border shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-2xl p-3 flex items-center justify-between gap-3 text-xs animate-in slide-in-from-top-3 duration-200 ${
            incomingAlert.isHumanRequested
              ? 'bg-[#201518]/95 border-rose-500/80 shadow-rose-500/20'
              : incomingAlert.isTakeover
              ? 'bg-[#201d14]/95 border-amber-500/80 shadow-amber-500/20'
              : 'bg-[#202c33]/95 border-[#00a884]/60'
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 shadow-md ${
                incomingAlert.isHumanRequested
                  ? 'bg-rose-500 text-white'
                  : incomingAlert.isTakeover
                  ? 'bg-amber-400 text-[#111b21]'
                  : 'bg-[#00a884] text-[#111b21]'
              }`}
            >
              <BellRing size={16} className="animate-pulse" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-white truncate flex items-center gap-1.5">
                <span>{incomingAlert.phone}</span>
                {incomingAlert.isHumanRequested ? (
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-rose-500/20 text-rose-300 font-bold border border-rose-500/40 animate-pulse">
                    ⚠️ Need Attention
                  </span>
                ) : incomingAlert.isTakeover ? (
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-amber-500/20 text-amber-300 font-bold border border-amber-500/40">
                    Takeover Active
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-[#00a884]/20 text-[#00a884] font-normal border border-[#00a884]/30">
                    New Customer Msg
                  </span>
                )}
              </p>
              <p className="text-[#8696a0] truncate text-[11px]">{incomingAlert.text}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => {
                setSelectedSessionId(incomingAlert.sessionId);
                setIncomingAlert(null);
              }}
              className={`px-3 py-1 rounded-lg font-bold text-[11px] transition shadow cursor-pointer active:scale-95 ${
                incomingAlert.isHumanRequested
                  ? 'bg-rose-500 hover:bg-rose-600 text-white'
                  : incomingAlert.isTakeover
                  ? 'bg-amber-500 hover:bg-amber-600 text-[#111b21]'
                  : 'bg-[#00a884] hover:bg-[#00a884]/90 text-[#111b21]'
              }`}
            >
              Open
            </button>
            <button
              type="button"
              onClick={() => setIncomingAlert(null)}
              className="p-1 text-[#8696a0] hover:text-white rounded-lg cursor-pointer"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* WHATSAPP WEB MASTER HEADER (HIDDEN ON MOBILE WHEN CHAT IS OPEN)   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <div
        className={`bg-[#202c33] px-3.5 py-2.5 items-center justify-between border-b border-[#2a3942] shrink-0 z-20 ${
          activeSession ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-full bg-[#00a884] flex items-center justify-center text-white shadow-md shrink-0">
            <Smartphone size={17} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-white tracking-wide truncate">
                WhatsApp Web
              </span>
              <span className="hidden sm:inline-flex text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#00a884]/20 text-[#00a884] border border-[#00a884]/30 font-semibold shrink-0">
                Cloud API
              </span>
            </div>
            <p className="text-[11px] text-[#8696a0] flex items-center gap-1.5 truncate">
              <span className="hidden sm:inline">{tenant?.name || 'Store'} • Live Inbox •</span>
              <span className="text-[#00a884] font-medium flex items-center gap-1 shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00a884] animate-pulse" />
                Active Session
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 relative shrink-0">
          {/* Incoming Customer Message Alerts Toggle (Sound + Browser Notification) */}
          <button
            type="button"
            onClick={handleToggleAlerts}
            title={alertsEnabled ? 'Message Alerts Active (Sound & Push Enabled)' : 'Turn ON Message Alerts (Audio Chime & Push)'}
            className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition cursor-pointer active:scale-95 ${
              alertsEnabled
                ? 'bg-[#00a884]/20 text-[#00a884] border border-[#00a884]/40 shadow-sm'
                : 'bg-[#111b21] text-[#8696a0] hover:text-[#d1d7db] border border-[#2a3942]'
            }`}
          >
            {alertsEnabled ? (
              <BellRing size={14} className="text-[#00a884] animate-bounce shrink-0" />
            ) : (
              <BellOff size={14} className="text-[#8696a0] shrink-0" />
            )}
            <span className="hidden sm:inline text-[11px]">
              {alertsEnabled ? 'Alerts ON' : 'Alerts OFF'}
            </span>
          </button>

          {/* PWA Install Button (Add to Home Screen) */}
          {!isAppInstalled && (
            <button
              type="button"
              onClick={handleInstallApp}
              title="Install WhatsApp Web as an App (Add to Home Screen)"
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-[#111b21] hover:bg-[#2a3942] text-[#00a884] hover:text-white border border-[#00a884]/40 text-xs font-bold transition cursor-pointer shadow-sm active:scale-95"
            >
              <Download size={14} className="text-[#00a884]" />
              <span className="hidden sm:inline">Install App</span>
            </button>
          )}

          {/* Access Passes & QR Code Governance (STRICT: Master Admin Tenet Hub Only, Hidden in Standalone / Magic Pass view) */}
          {!standalone && (
            <button
              type="button"
              onClick={() => setShowMobileModal(true)}
              title="Mobile Access Passes & QR Code Governance"
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-[#111b21] hover:bg-[#2a3942] text-[#00a884] hover:text-white border border-[#00a884]/40 text-xs font-bold transition cursor-pointer shadow-sm active:scale-95"
            >
              <QrCode size={14} className="text-[#00a884]" />
              <span className="hidden sm:inline">Access Passes</span>
            </button>
          )}

          {/* Refresh Button */}
          <button
            type="button"
            onClick={fetchChats}
            title="Refresh Conversations"
            className="p-1.5 sm:p-2 rounded-full hover:bg-[#374248] text-[#aebac1] hover:text-white transition cursor-pointer"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin text-[#00a884]' : ''} />
          </button>

          {/* Clear All WhatsApp Chats Button */}
          <button
            type="button"
            onClick={() => setShowClearAllModal(true)}
            disabled={sessions.length === 0}
            title="Clear all WhatsApp chats & reset Luna context"
            className="p-1.5 sm:p-2 rounded-full hover:bg-rose-500/20 text-[#aebac1] hover:text-rose-400 transition cursor-pointer disabled:opacity-30 disabled:pointer-events-none"
          >
            <Trash2 size={16} />
          </button>

          {/* Fullscreen Toggle (only in embed mode) */}
          {!standalone && (
            <button
              type="button"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              className="hidden sm:block p-1.5 sm:p-2 rounded-full hover:bg-[#374248] text-[#aebac1] hover:text-white transition cursor-pointer"
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          )}

          {/* Standalone Dropdown Menu Button */}
          {standalone && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMenu(!showMenu)}
                title="Options"
                className="p-1.5 sm:p-2 rounded-full hover:bg-[#374248] text-[#aebac1] hover:text-white transition cursor-pointer"
              >
                <MoreVertical size={17} />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-[#202c33] border border-[#2a3942] rounded-xl shadow-2xl py-2 z-50 text-xs text-[#d1d7db] animate-in fade-in zoom-in-95 duration-100">
                  {!isAppInstalled && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowMenu(false);
                        handleInstallApp();
                      }}
                      className="w-full px-4 py-2.5 flex items-center gap-2.5 hover:bg-[#111b21] text-left transition cursor-pointer text-[#00a884]"
                    >
                      <Download size={15} />
                      <span>Install App (Add to Home Screen)</span>
                    </button>
                  )}
                  {typeof window !== 'undefined' && !localStorage.getItem('tenet_is_magic_pass') && (
                    <a
                      href="/tenet"
                      onClick={() => setShowMenu(false)}
                      className="w-full px-4 py-2.5 flex items-center gap-2.5 hover:bg-[#111b21] text-left transition cursor-pointer"
                    >
                      <LayoutDashboard size={15} className="text-[#aebac1]" />
                      <span>Switch to Full Tenet Admin</span>
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenu(false);
                      setShowClearAllModal(true);
                    }}
                    disabled={sessions.length === 0}
                    className="w-full px-4 py-2.5 flex items-center gap-2.5 hover:bg-rose-500/10 text-rose-400 text-left transition cursor-pointer disabled:opacity-40"
                  >
                    <Trash2 size={15} />
                    <span>Clear All WhatsApp Chats</span>
                  </button>
                  <div className="my-1 border-t border-[#2a3942]" />
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenu(false);
                      if (onLogout) {
                        onLogout();
                      } else {
                        localStorage.removeItem('tenet_client_token');
                        localStorage.removeItem('tenet_token');
                        sessionStorage.removeItem('tenet_client_token');
                        sessionStorage.removeItem('tenet_token');
                        window.location.href = '/whatsapp';
                      }
                    }}
                    className="w-full px-4 py-2.5 flex items-center gap-2.5 hover:bg-rose-500/10 text-rose-400 text-left transition cursor-pointer"
                  >
                    <LogOut size={15} />
                    <span>Log Out</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Close Window Button (if provided and not standalone) */}
          {onClose && !standalone && (
            <button
              type="button"
              onClick={onClose}
              title="Close WhatsApp Web"
              className="p-1.5 sm:p-2 rounded-full hover:bg-rose-500/20 text-[#aebac1] hover:text-rose-400 transition cursor-pointer"
            >
              <X size={17} />
            </button>
          )}
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* 2-COLUMN WHATSAPP INTERFACE (MOBILE RESPONSIVE)                   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex overflow-hidden relative">
        
        {/* ── LEFT COLUMN: CONVERSATION LIST (Screen 1 on Mobile) ── */}
        <div
          className={`w-full md:w-[380px] lg:w-[420px] bg-[#111b21] flex flex-col border-r border-[#222d34] shrink-0 ${
            activeSession ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* Multi-Account Channel Switcher Dropdown */}
          {connectedInstances.length > 1 && (
            <div className="px-3 py-2 bg-[#182229] border-b border-[#222d34] flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5 text-[#8696a0] text-[11px] font-semibold shrink-0">
                <Smartphone size={13} className="text-[#00a884]" />
                <span className="hidden sm:inline">Account:</span>
              </div>
              <select
                value={selectedAccountFilter}
                onChange={(e) => setSelectedAccountFilter(e.target.value)}
                className="bg-[#111b21] text-white text-xs rounded-xl px-2.5 py-1.5 border border-[#2a3942] focus:outline-none focus:border-[#00a884] font-medium w-full truncate cursor-pointer shadow-inner"
              >
                <option value="all">🌐 All Numbers / Unified Inbox ({sessions.length})</option>
                {connectedInstances.map((inst, idx) => {
                  const instKey = inst.nodeId || inst.metaPhoneNumberId || inst.phoneNumber || `inst_${idx}`;
                  const filterValue = inst.nodeId || inst.metaPhoneNumberId || inst.phoneNumber;
                  const rawDigits = (inst.phoneNumber || '').replace(/\D/g, '');
                  const displayPhone = rawDigits ? (String(inst.phoneNumber).startsWith('+') ? String(inst.phoneNumber).trim() : `+${rawDigits}`) : (inst.metaPhoneNumberId || `Account ${idx + 1}`);
                  const count = sessions.filter((s) => {
                    const matchNodeId = inst.nodeId && (s as any).instanceNodeId === inst.nodeId;
                    const matchInstId = inst.metaPhoneNumberId && s.instancePhoneNumberId === inst.metaPhoneNumberId;
                    const matchInstPhone = rawDigits.length >= 8 && s.instancePhoneNumber && s.instancePhoneNumber.replace(/\D/g, '').includes(rawDigits);
                    const matchSessionId = rawDigits.length >= 8 && s.sessionId && s.sessionId.includes(rawDigits);
                    return matchNodeId || matchInstId || matchInstPhone || matchSessionId;
                  }).length;
                  const icon = inst.isPersonal ? '📱 ' : '🏢 ';
                  const customLabel = (inst.displayName || inst.label || '').trim();
                  const optionLabel = customLabel && !customLabel.startsWith('tenet_')
                    ? `${displayPhone} (${customLabel})`
                    : displayPhone;
                  return (
                    <option
                      key={instKey}
                      value={filterValue}
                    >
                      {icon} {optionLabel} — {count} chats
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* WhatsApp Search Bar */}
          <div className="p-2.5 bg-[#111b21] border-b border-[#222d34] space-y-2">
            <div className="relative flex items-center bg-[#202c33] rounded-lg px-3 py-1.5 focus-within:ring-1 focus-within:ring-[#00a884]">
              <Search size={16} className="text-[#8696a0] mr-3 shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search or start new chat"
                className="w-full bg-transparent text-xs text-[#d1d7db] placeholder-[#8696a0] focus:outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-[#8696a0] hover:text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Filter Pills (All, Orders, Leads, Unread, Voice, Takeover) */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 text-[11px] font-medium scrollbar-none">
              <button
                type="button"
                onClick={() => setFilterTab('all')}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition cursor-pointer ${
                  filterTab === 'all'
                    ? 'bg-[#00a884] text-[#111b21] font-bold'
                    : 'bg-[#202c33] text-[#8696a0] hover:text-[#d1d7db]'
                }`}
              >
                All ({Math.max(accountFilteredSessions.length, serverTotalChatsCount || 0)})
              </button>
              <button
                type="button"
                onClick={() => setFilterTab('orders')}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition flex items-center gap-1 cursor-pointer ${
                  filterTab === 'orders'
                    ? 'bg-emerald-400 text-slate-950 font-bold shadow-md shadow-emerald-500/30'
                    : 'bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/40'
                }`}
              >
                <ShoppingBag size={11} className={filterTab === 'orders' ? 'text-slate-950' : 'text-emerald-400'} />
                <span>Orders ({displayOrdersCount})</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterTab('leads')}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition flex items-center gap-1 cursor-pointer ${
                  filterTab === 'leads'
                    ? 'bg-sky-400 text-slate-950 font-bold shadow-md shadow-sky-500/30'
                    : 'bg-sky-950/40 border border-sky-500/30 text-sky-400 hover:text-sky-300 hover:bg-sky-900/40'
                }`}
              >
                <Sparkles size={11} className={filterTab === 'leads' ? 'text-slate-950' : 'text-sky-400'} />
                <span>Leads ({leadsCount})</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterTab('unread')}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition cursor-pointer ${
                  filterTab === 'unread'
                    ? 'bg-[#00a884] text-[#111b21] font-bold'
                    : 'bg-[#202c33] text-[#8696a0] hover:text-[#d1d7db]'
                }`}
              >
                Unread
              </button>
              <button
                type="button"
                onClick={() => setFilterTab('takeover')}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition flex items-center gap-1 cursor-pointer ${
                  filterTab === 'takeover'
                    ? 'bg-amber-500 text-[#111b21] font-bold'
                    : 'bg-[#202c33] text-[#8696a0] hover:text-amber-300'
                }`}
              >
                <Clock size={11} />
                <span>Takeover</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterTab('voice')}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition flex items-center gap-1 cursor-pointer ${
                  filterTab === 'voice'
                    ? 'bg-purple-500 text-white font-bold'
                    : 'bg-[#202c33] text-[#8696a0] hover:text-purple-300'
                }`}
              >
                <PhoneCall size={11} />
                <span>Voice Calls</span>
              </button>
            </div>

            {/* Order Timeframe & Revenue HUD Filter */}
            {filterTab === 'orders' && (
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-[#182229] border border-emerald-500/30 rounded-xl text-[11px] font-mono mt-1">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setOrderCycleFilter('all')}
                    className={`px-2 py-0.5 rounded-lg font-bold transition cursor-pointer ${
                      orderCycleFilter === 'all'
                        ? 'bg-emerald-500 text-slate-950 font-black shadow-sm'
                        : 'bg-[#202c33] text-emerald-300 hover:text-white'
                    }`}
                  >
                    All-Time ({displayOrdersCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrderCycleFilter('current_month')}
                    className={`px-2 py-0.5 rounded-lg font-bold transition cursor-pointer ${
                      orderCycleFilter === 'current_month'
                        ? 'bg-cyan-400 text-slate-950 font-black shadow-sm'
                        : 'bg-[#202c33] text-cyan-300 hover:text-white'
                    }`}
                  >
                    This Month ({currentMonthOrdersCount})
                  </button>
                </div>
                {orderMetrics?.totalOrdersAmount !== undefined && (
                  <span className="text-[10px] font-bold text-emerald-300 truncate">
                    💰 LKR {Number(orderCycleFilter === 'current_month' ? orderMetrics.currentMonthOrdersAmount : orderMetrics.totalOrdersAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Chat List Items */}
          <div className="flex-1 overflow-y-auto divide-y divide-[#222d34]/60">
            {filteredSessions.length === 0 ? (
              <div className="p-8 text-center text-[#8696a0] space-y-2">
                <Smartphone size={32} className="mx-auto opacity-30 text-[#00a884]" />
                <p className="text-xs">No WhatsApp conversations found.</p>
                <p className="text-[11px] text-[#8696a0]/70">
                  Customer messages arriving via Meta Cloud API will appear here instantly.
                </p>
              </div>
            ) : (
              filteredSessions.map((s) => {
                const isSelected = s.sessionId === selectedSessionId;
                const messages = s.messages || [];
                const lastMsg = messages[messages.length - 1];
                const customerMsgs = messages.filter(isCustomerMessage);
                const lastCustMsg = customerMsgs[customerMsgs.length - 1];

                const isVoice = s.isVoiceCall || s.channel === 'sip_voice' || s.sessionId.startsWith('call_');
                const isTaken = s.isHumanTakeover || s.status === 'human_takeover';
                const isRequested =
                  s.humanRequested ||
                  s.status === 'human_requested' ||
                  isCustomerRequestingHuman(lastCustMsg?.text);

                return (
                  <div
                    key={s.sessionId}
                    onClick={() => setSelectedSessionId(s.sessionId)}
                    className={`group px-3.5 py-3 flex items-center gap-3 transition cursor-pointer relative ${
                      s.hasOrder
                        ? 'border-l-4 border-emerald-400 bg-emerald-500/[0.08]'
                        : s.hasLead
                        ? 'border-l-4 border-sky-400 bg-sky-500/[0.06]'
                        : isRequested
                        ? 'border-l-4 border-rose-500 bg-rose-500/[0.06]'
                        : isTaken
                        ? 'border-l-4 border-amber-500 bg-amber-500/[0.04]'
                        : ''
                    } ${
                      isSelected
                        ? 'bg-[#2a3942]'
                        : 'hover:bg-[#202c33]/70 bg-transparent'
                    }`}
                  >
                    {/* User Avatar */}
                    <div className="relative shrink-0">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm ${
                        s.hasOrder
                          ? 'bg-gradient-to-br from-emerald-600 to-teal-800 ring-2 ring-emerald-400/40'
                          : s.hasLead
                          ? 'bg-gradient-to-br from-sky-600 to-indigo-800 ring-2 ring-sky-400/40'
                          : 'bg-gradient-to-br from-[#128c7e] to-[#075e54]'
                      }`}>
                        {s.phone ? s.phone.slice(-2) : s.visitorName.slice(0, 2).toUpperCase()}
                      </div>
                      {/* Active Indicator */}
                      <span
                        className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-[#111b21] ${
                          s.hasOrder
                            ? 'bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
                            : s.hasLead
                            ? 'bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]'
                            : isVoice
                            ? 'bg-purple-400'
                            : isRequested
                            ? 'bg-rose-500 animate-pulse'
                            : isTaken
                            ? 'bg-amber-400'
                            : 'bg-[#00a884]'
                        }`}
                      />
                    </div>

                    {/* Chat Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="text-sm font-semibold text-[#e9edef] truncate">
                            {s.phone ? formatPhoneNumber(s.phone) : s.visitorName}
                          </span>
                          {s.hasOrder && (
                            <span className="text-[8px] font-mono font-black px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-400/50 shrink-0 shadow-[0_0_8px_rgba(16,185,129,0.3)] flex items-center gap-0.5">
                              <ShoppingBag size={8} /> ORDER
                            </span>
                          )}
                          {s.hasLead && !s.hasOrder && (
                            <span className="text-[8px] font-mono font-black px-1.5 py-0.2 rounded bg-sky-500/20 text-sky-300 border border-sky-400/50 shrink-0 shadow-[0_0_8px_rgba(56,189,248,0.3)] flex items-center gap-0.5">
                              <Sparkles size={8} /> LEAD
                            </span>
                          )}
                          {s.instanceLabel && connectedInstances.length > 1 && (
                            <span className={`text-[8px] font-mono font-semibold px-1.5 py-0.2 rounded border shrink-0 flex items-center gap-0.5 ${
                              s.isPersonalInstance
                                ? 'bg-purple-500/20 text-purple-300 border-purple-400/40'
                                : 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40'
                            }`}>
                              <span>{s.isPersonalInstance ? '📱' : '🏢'}</span>
                              <span>{s.instancePhoneNumber ? `+${s.instancePhoneNumber.replace(/\D/g, '')}` : s.instanceLabel}</span>
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-[#8696a0] font-mono shrink-0 ml-1">
                          {lastMsg?.time || 'Recent'}
                        </span>
                      </div>

                      {/* User Profile / Customer Name Line Underneath Phone — ONLY render if a genuine human name exists! Never show dummy placeholders */}
                      {Boolean(s.phone && getDisplayCustomerName(s)) && (
                        <div className="flex items-center gap-1 text-[11px] text-slate-300 font-medium truncate mt-0.5">
                          <span className="text-slate-400 text-[10px]">👤</span>
                          <span className="truncate text-[#c5cdd3]">
                            {getDisplayCustomerName(s)}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-[#8696a0] truncate flex items-center gap-1">
                          {/* Authentic Receipt indicator for outgoing last message */}
                          {lastMsg && !isCustomerMessage(lastMsg) && (
                            <WhatsAppReceipt status={lastMsg?.status || 'delivered'} className="shrink-0" />
                          )}
                          {isVoice && <PhoneCall size={12} className="text-purple-400 shrink-0" />}
                          {(lastMsg?.isAudio || lastMsg?.isVoiceTurn || lastMsg?.audioUrl) && (
                            <Mic
                              size={13}
                              className={lastMsg?.played ? 'text-[#53bdeb] shrink-0' : 'text-[#8696a0] shrink-0'}
                            />
                          )}
                          <span className="truncate">
                            {isRequested ? (
                              <span className="text-rose-400 font-semibold flex items-center gap-1">
                                <AlertCircle size={12} className="shrink-0" />
                                <span>Customer requested human</span>
                              </span>
                            ) : (lastMsg?.isAudio || lastMsg?.isVoiceTurn || lastMsg?.audioUrl) ? (
                              'Voice message'
                            ) : s.hasOrder && s.latestOrder ? (
                              <span className="text-emerald-300 font-medium flex items-center gap-1">
                                <span className="font-mono text-[10px] font-black px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                  {s.latestOrder.orderNumber ? `ORD-${s.latestOrder.orderNumber}` : 'ORDER'}
                                </span>
                                <span className="text-emerald-200">
                                  {s.latestOrder.currency || 'LKR'} {Number(s.latestOrder.totalAmount || 0).toLocaleString()}
                                </span>
                                {s.latestOrder.status && (
                                  <span className="text-[9px] text-emerald-400/80 font-mono">
                                    • {s.latestOrder.status}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <>
                                {lastMsg && !isCustomerMessage(lastMsg) && (
                                  <span className="text-[#00a884] font-medium mr-1">
                                    {isLunaMessage(lastMsg) ? `${tenant?.agentName || 'Luna AI'}:` : 'You:'}
                                  </span>
                                )}
                                {lastMsg ? (
                                  lastMsg.text || (isVoice ? 'Voice Call interaction' : 'Inquiry initiated')
                                ) : s.hasLead && s.latestLead ? (
                                  <span className="text-sky-300 font-medium flex items-center gap-1">
                                    <span className="font-mono text-[10px] font-black px-1 py-0.2 rounded bg-sky-500/20 text-sky-300 border border-sky-500/40">
                                      {s.latestLead.formType || 'LEAD'}
                                    </span>
                                    <span className="text-sky-200 truncate">
                                      {s.latestLead.notes || s.latestLead.status || 'Lead captured'}
                                    </span>
                                  </span>
                                ) : (
                                  isVoice ? 'Voice Call interaction' : 'Inquiry initiated'
                                )}
                              </>
                            )}
                          </span>
                        </p>

                        {/* Status Badge */}
                        <div className="shrink-0 ml-2">
                          {s.hasOrder ? (
                            <span className="text-[9px] font-mono font-black px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 flex items-center gap-1 shadow-sm">
                              <ShoppingBag size={10} className="shrink-0 text-emerald-400" />
                              <span>{s.latestOrder?.orderNumber ? s.latestOrder.orderNumber : 'ORDER'}</span>
                            </span>
                          ) : s.hasLead ? (
                            <span className="text-[9px] font-mono font-black px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/50 flex items-center gap-1 shadow-sm">
                              <Sparkles size={10} className="shrink-0 text-sky-400" />
                              <span>LEAD</span>
                            </span>
                          ) : isRequested ? (
                            <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 flex items-center gap-1 animate-pulse">
                              <AlertCircle size={10} className="shrink-0" />
                              NEED ATTENTION
                            </span>
                          ) : isTaken ? (
                            <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-1">
                              <Clock size={10} className="shrink-0" />
                              TAKEOVER
                            </span>
                          ) : (
                            <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#00a884]/20 text-[#00a884] border border-[#00a884]/40">
                              AI ACTIVE
                            </span>
                          )}

                          {/* Quick Delete Chat Button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmTarget({
                                sessionId: s.sessionId,
                                name: getDisplayCustomerName(s) || s.visitorName || 'Customer',
                                phone: s.phone
                              });
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-rose-500/20 text-[#8696a0] hover:text-rose-400 transition cursor-pointer"
                            title="Delete conversation"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── RIGHT COLUMN: AUTHENTIC CHAT ROOM (Screen 2 on Mobile) ── */}
        <div
          className={`flex-1 flex flex-col bg-[#0b141a] relative overflow-hidden ${
            !activeSession ? 'hidden md:flex' : 'flex'
          }`}
        >
          {activeSession ? (
            <>
              {/* ── Authentic WhatsApp Chat Header ── */}
              <div className="bg-[#202c33] px-2.5 sm:px-4 py-2 flex items-center justify-between border-b border-[#2a3942] z-10 shrink-0 h-14 sm:h-16">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  {/* Mobile Back Button (< 768px) */}
                  <button
                    type="button"
                    onClick={() => setSelectedSessionId(null)}
                    className="md:hidden p-1.5 rounded-full hover:bg-[#374248] text-[#aebac1] hover:text-white active:scale-95 transition cursor-pointer shrink-0"
                    title="Back to all chats"
                  >
                    <ArrowLeft size={20} />
                  </button>

                  <div className="relative shrink-0">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#128c7e] to-[#075e54] flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-sm">
                      {activeSession.phone ? activeSession.phone.slice(-2) : activeSession.visitorName.slice(0, 2).toUpperCase()}
                    </div>
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#202c33] ${
                        isTakeoverActive
                          ? 'bg-amber-400'
                          : activeSession.humanRequested ||
                            isCustomerRequestingHuman(
                              activeSession.messages?.filter(isCustomerMessage).slice(-1)[0]?.text
                            )
                          ? 'bg-rose-500 animate-pulse'
                          : 'bg-[#00a884]'
                      }`}
                    />
                  </div>

                  <div className="min-w-0 flex-1 pr-1">
                    <div className="flex items-center gap-2 truncate">
                      <h3 className="text-xs sm:text-sm font-semibold text-[#e9edef] truncate tracking-wide">
                        {activeSession.phone ? formatPhoneNumber(activeSession.phone) : (getDisplayCustomerName(activeSession) || activeSession.visitorName)}
                      </h3>
                      {activeSession.phone && getDisplayCustomerName(activeSession) && (
                        <span className="px-2 py-0.5 rounded-full bg-white/10 text-[10px] text-slate-200 font-medium flex items-center gap-1 shrink-0 border border-white/10">
                          <span>👤</span> {getDisplayCustomerName(activeSession)}
                        </span>
                      )}
                      {activeSession.instanceLabel && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-medium flex items-center gap-1 shrink-0 border ${
                          activeSession.isPersonalInstance
                            ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                            : 'bg-[#00a884]/20 text-[#00a884] border-[#00a884]/30'
                        }`}>
                          <span>{activeSession.isPersonalInstance ? '📱' : '🏢'}</span>
                          <span>{activeSession.instancePhoneNumber ? formatPhoneNumber(activeSession.instancePhoneNumber) : activeSession.instanceLabel}</span>
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] sm:text-[11px] truncate flex items-center gap-1.5 mt-0.5">
                      {isTakeoverActive ? (
                        <span className="text-amber-400 font-mono font-medium flex items-center gap-1 truncate">
                          <Clock size={11} className="animate-pulse shrink-0" />
                          <span className="truncate">
                            Takeover {takeoverCountdown !== null ? `(${formatCountdown(takeoverCountdown)})` : 'Active'}
                          </span>
                        </span>
                      ) : activeSession.humanRequested ||
                        isCustomerRequestingHuman(
                          activeSession.messages?.filter(isCustomerMessage).slice(-1)[0]?.text
                        ) ? (
                        <span className="text-rose-400 font-bold flex items-center gap-1 truncate animate-pulse">
                          <AlertCircle size={11} className="shrink-0" />
                          <span className="truncate">Need Attention (Customer Requested Human)</span>
                        </span>
                      ) : (
                        <span className="text-[#00a884] flex items-center gap-1 truncate">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00a884] shrink-0" />
                          <span className="truncate">{tenant?.agentName || 'Luna AI'} Replying Automatically</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Header Actions & Human Takeover Toggle */}
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  {isTakeoverActive ? (
                    <button
                      type="button"
                      disabled={toggling}
                      onClick={() => handleToggleTakeover('timed_30m', false)}
                      className="px-2.5 sm:px-3 py-1.5 rounded-full bg-[#00a884] hover:bg-[#00a884]/90 active:scale-95 text-[#111b21] text-[11px] sm:text-xs font-black transition flex items-center gap-1 sm:gap-1.5 cursor-pointer shadow-md"
                      title="Resume AI automatic responses"
                    >
                      <Bot size={13} className="shrink-0" />
                      <span className="whitespace-nowrap">Resume AI</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={toggling}
                      onClick={() => handleToggleTakeover('timed_30m', true)}
                      className="px-2.5 sm:px-3 py-1.5 rounded-full bg-[#111b21] hover:bg-[#2a3942] border border-amber-500/50 text-amber-300 active:scale-95 text-[11px] sm:text-xs font-bold transition flex items-center gap-1 sm:gap-1.5 cursor-pointer shadow-sm"
                      title="Pause Luna AI and take over chatting for 30 minutes"
                    >
                      <User size={13} className="shrink-0 text-amber-400" />
                      <span className="whitespace-nowrap">Take Over</span>
                    </button>
                  )}

                  {/* Quick Call Icon */}
                  {activeSession.phone && (
                    <a
                      href={`tel:+${activeSession.phone}`}
                      className="p-1.5 sm:p-2 rounded-full hover:bg-[#374248] text-[#aebac1] hover:text-white transition cursor-pointer"
                      title={`Call +${activeSession.phone}`}
                    >
                      <Phone size={16} />
                    </a>
                  )}

                  {/* Delete Conversation Button */}
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmTarget({
                      sessionId: activeSession.sessionId,
                      name: getDisplayCustomerName(activeSession) || activeSession.visitorName || 'Customer',
                      phone: activeSession.phone
                    })}
                    className="p-1.5 sm:p-2 rounded-full hover:bg-rose-500/20 text-[#aebac1] hover:text-rose-400 transition cursor-pointer"
                    title="Delete conversation & clear Luna context"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* ── Prominent Lead / Order Captured Banner ── */}
              {activeSession.hasOrder ? (
                <div className="bg-gradient-to-r from-emerald-950/95 via-[#072418] to-teal-950/95 px-4 py-2 border-b border-emerald-500/30 flex items-center justify-between text-xs font-mono shrink-0 z-10 shadow-md">
                  <div className="flex items-center gap-2.5 truncate">
                    <span className="p-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shrink-0">
                      <ShoppingBag size={14} />
                    </span>
                    <div className="truncate">
                      <span className="text-emerald-200 font-bold">
                        🛍️ Order Captured: <span className="text-white underline">{activeSession.latestOrder?.orderNumber || 'CONFIRMED'}</span>
                        {activeSession.latestOrder?.totalAmount && (
                          <span className="ml-1 text-emerald-300 font-black">
                            ({activeSession.latestOrder?.currency || 'LKR'} {Number(activeSession.latestOrder.totalAmount).toLocaleString()})
                          </span>
                        )}
                        {activeSession.latestOrder?.status && (
                          <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/30 uppercase text-emerald-300 font-bold">
                            {activeSession.latestOrder.status}
                          </span>
                        )}
                      </span>
                      {activeSession.latestOrder?.itemsSummary && (
                        <p className="text-[10px] text-slate-400 font-sans truncate mt-0.5">
                          Items: {activeSession.latestOrder.itemsSummary}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-[9px] font-black px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shrink-0 ml-2 shadow-[0_0_10px_rgba(16,185,129,0.3)]">
                    VERIFIED SALE
                  </span>
                </div>
              ) : activeSession.hasLead ? (
                <div className="bg-gradient-to-r from-sky-950/95 via-[#081f2f] to-indigo-950/95 px-4 py-2 border-b border-sky-500/30 flex items-center justify-between text-xs font-mono shrink-0 z-10 shadow-md">
                  <div className="flex items-center gap-2.5 truncate">
                    <span className="p-1 rounded-lg bg-sky-500/20 text-sky-300 border border-sky-500/40 shrink-0">
                      <Sparkles size={14} />
                    </span>
                    <div className="truncate">
                      <span className="text-sky-200 font-bold">
                        🎯 Lead Captured: <span className="text-white">{activeSession.latestLead?.formType || 'Customer Lead'}</span>
                        {activeSession.latestLead?.status && (
                          <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-sky-500/20 border border-sky-500/30 uppercase text-sky-300 font-bold">
                            {activeSession.latestLead.status}
                          </span>
                        )}
                      </span>
                      {activeSession.latestLead?.notes && (
                        <p className="text-[10px] text-slate-400 font-sans truncate mt-0.5">
                          {activeSession.latestLead.notes.slice(0, 70)}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-[9px] font-black px-2.5 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/40 shrink-0 ml-2 shadow-[0_0_10px_rgba(56,189,248,0.3)]">
                    HOT LEAD
                  </span>
                </div>
              ) : null}

              {/* ── WhatsApp Doodle Wallpaper Chat Area ── */}
              <div
                ref={chatScrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-3 relative"
                style={{
                  backgroundColor: '#0b141a',
                  backgroundImage: `radial-gradient(#111b21 1.5px, transparent 1.5px), radial-gradient(#182229 1.5px, #0b141a 1.5px)`,
                  backgroundSize: '24px 24px',
                  backgroundPosition: '0 0, 12px 12px',
                }}
              >
                {/* Date Divider */}
                <div className="flex justify-center my-2">
                  <span className="px-3 py-1 rounded-lg bg-[#182229] border border-[#222d34] text-[11px] font-mono text-[#8696a0] shadow-sm">
                    TODAY
                  </span>
                </div>

                {/* Encryption Notice */}
                <div className="flex justify-center my-1">
                  <div className="max-w-md px-4 py-2 rounded-lg bg-[#182229]/90 border border-[#222d34] text-center text-[10px] text-[#ffd279] shadow-sm leading-relaxed flex items-center justify-center gap-1.5">
                    <Lock size={12} className="shrink-0 text-[#ffd279]" />
                    <span>Messages are end-to-end processed by Kangavena Sovereign Luna AI.</span>
                  </div>
                </div>

                {/* Messages Stream */}
                {activeSession.messages.map((m, idx) => {
                  const isIncoming = m.sender === 'visitor';
                  const isHumanAdmin = m.sender === 'human_admin';
                  const isLuna = m.sender === 'luna' || (!isIncoming && !isHumanAdmin);
                  const isVoiceMsg = Boolean(
                    m.isAudio ||
                    m.audioUrl ||
                    m.isVoiceTurn ||
                    m.mediaType === 'audio' ||
                    (m.mediaUrl && /\.(ogg|mp3|wav|m4a|aac|opus)$/i.test(m.mediaUrl)) ||
                    (Array.isArray((m as any).dispatchedMedia) && (m as any).dispatchedMedia.some((u: string) => /\.(ogg|mp3|wav|m4a|aac|opus)$/i.test(u))) ||
                    (Array.isArray((m as any).mediaUrls) && (m as any).mediaUrls.some((u: string) => /\.(ogg|mp3|wav|m4a|aac|opus)$/i.test(u))) ||
                    (typeof m.text === 'string' && m.text.includes('[Voice Note Transcription]'))
                  );

                  const resolvedAudioUrl = m.audioUrl
                    || (m.mediaUrl && /\.(ogg|mp3|wav|m4a|aac|opus)$/i.test(m.mediaUrl) ? m.mediaUrl : null)
                    || (Array.isArray((m as any).dispatchedMedia) && (m as any).dispatchedMedia.find((u: string) => /\.(ogg|mp3|wav|m4a|aac|opus)$/i.test(u)))
                    || (Array.isArray((m as any).mediaUrls) && (m as any).mediaUrls.find((u: string) => /\.(ogg|mp3|wav|m4a|aac|opus)$/i.test(u)))
                    || (m.mediaType === 'audio' && m.mediaUrl ? m.mediaUrl : null)
                    || null;

                  return (
                    <div
                      key={m.clientMsgId || idx}
                      className={`flex flex-col ${isIncoming ? 'items-start' : 'items-end'}`}
                    >
                      {/* Message Bubble */}
                      <div
                        className={`relative max-w-[85%] md:max-w-[70%] px-3.5 py-2 rounded-2xl text-[13px] leading-relaxed shadow-md ${
                          isIncoming
                            ? 'bg-[#202c33] text-[#e9edef] rounded-tl-sm border border-[#2a3942]/60'
                            : 'bg-[#005c4b] text-[#e9edef] rounded-tr-sm border border-[#007a60]/50'
                        }`}
                      >
                        {/* Outgoing Role Indicator */}
                        {!isIncoming && (
                          <div className="flex items-center gap-1 mb-1 text-[10px] text-[#8696a0] font-mono font-bold">
                            {isHumanAdmin ? (
                              <span className="text-amber-300 flex items-center gap-1">
                                <ShieldCheck size={11} /> Admin Takeover
                              </span>
                            ) : (
                              <span className="text-[#00a884] flex items-center gap-1">
                                <Bot size={11} /> Luna AI
                              </span>
                            )}
                          </div>
                        )}

                        {/* If Voice Note: render authentic WhatsApp Voice Note Player */}
                        {isVoiceMsg ? (
                          <WhatsAppVoiceNotePlayer
                            audioUrl={resolvedAudioUrl}
                            text={m.text}
                            duration={m.duration}
                            played={m.played}
                            isIncoming={isIncoming}
                            time={m.time}
                            status={m.status || (isHumanAdmin ? 'delivered' : 'read')}
                            token={token}
                            sessionId={activeSession.sessionId}
                            messageIndex={idx}
                            onMarkPlayed={() => {
                              setSessions((prev) =>
                                prev.map((s) =>
                                  s.sessionId === activeSession.sessionId
                                    ? {
                                        ...s,
                                        messages: s.messages.map((item, i) =>
                                          i === idx ? { ...item, played: true } : item
                                        ),
                                      }
                                    : s
                                )
                              );
                            }}
                          />
                        ) : (
                          <>
                            {/* Dispatched Photos & Media Attachments */}
                            {(m.mediaUrl || (m.mediaUrls && m.mediaUrls.length > 0) || (m.dispatchedMedia && m.dispatchedMedia.length > 0)) && (
                              <div className="mb-2 space-y-1.5">
                                {((m.mediaUrls && m.mediaUrls.length > 0) ? m.mediaUrls : (m.dispatchedMedia && m.dispatchedMedia.length > 0) ? m.dispatchedMedia : [m.mediaUrl!]).map((url: string, imgIdx: number) => {
                                  if (!url) return null;
                                  const isPdf = url.toLowerCase().endsWith('.pdf');
                                  const isVideo = ['.mp4', '.mov', '.webm'].some(ext => url.toLowerCase().endsWith(ext));
                                  const fullMediaUrl = url.startsWith('http') ? url : `${typeof window !== 'undefined' ? window.location.origin : ''}${url}`;

                                  if (isPdf) {
                                    return (
                                      <a
                                        key={imgIdx}
                                        href={fullMediaUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900/90 hover:bg-slate-900 border border-white/10 transition cursor-pointer text-white no-underline shadow-sm"
                                      >
                                        <FileText size={20} className="text-rose-400 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-bold truncate text-white">{url.split('/').pop() || 'Document.pdf'}</p>
                                          <p className="text-[10px] text-slate-400">PDF Document • Click to open</p>
                                        </div>
                                        <ExternalLink size={13} className="text-slate-400" />
                                      </a>
                                    );
                                  }

                                  if (isVideo) {
                                    return (
                                      <video
                                        key={imgIdx}
                                        src={fullMediaUrl}
                                        controls
                                        className="rounded-xl max-h-60 w-full object-cover border border-white/10 shadow-md"
                                      />
                                    );
                                  }

                                  return (
                                    <div key={imgIdx} className="relative group rounded-xl overflow-hidden border border-white/10 shadow-md bg-black/40">
                                      <img
                                        src={fullMediaUrl}
                                        alt="Dispatched Media"
                                        className="max-h-64 w-full object-cover rounded-xl cursor-pointer hover:opacity-95 transition"
                                        onClick={() => window.open(fullMediaUrl, '_blank')}
                                        onError={(e) => {
                                          (e.target as HTMLElement).style.display = 'none';
                                        }}
                                      />
                                      <div
                                        onClick={() => window.open(fullMediaUrl, '_blank')}
                                        className="absolute bottom-1.5 right-1.5 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-[9px] font-mono text-white flex items-center gap-1 opacity-0 group-hover:opacity-100 transition cursor-pointer"
                                      >
                                        <span>Full Image</span>
                                        <ExternalLink size={10} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Text Message Content & Collapsible Extracted Vision/OCR Details */}
                            <MessageVisualContent
                              text={m.text}
                              isIncoming={isIncoming}
                              messageKey={`wa_msg_${m.clientMsgId || idx}`}
                              hasImages={Boolean(m.mediaUrl || (m.mediaUrls && m.mediaUrls.length > 0) || (m.dispatchedMedia && m.dispatchedMedia.length > 0))}
                              theme="whatsapp"
                            />

                            {/* Timestamp & Status Checkmarks */}
                            <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-[#8696a0] font-mono float-right ml-3">
                              <span>{m.time}</span>
                              {!isIncoming && (
                                <WhatsAppReceipt status={m.status || (isHumanAdmin ? 'delivered' : 'read')} />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div ref={messagesEndRef} className="h-1 shrink-0" />
              </div>

              {/* ── WhatsApp Authentic Bottom Input Bar ── */}
              <div className="bg-[#202c33] px-3 py-2.5 border-t border-[#2a3942] relative z-20 shrink-0">
                {/* Quick Emoji Drawer Popup */}
                {showEmojiPicker && (
                  <div className="absolute bottom-16 left-4 bg-[#111b21] border border-[#2a3942] rounded-2xl p-3 shadow-2xl flex flex-wrap gap-2 w-72 z-50">
                    {['👍', '❤️', '😊', '🙏', '🔥', '🎉', '✅', '🛍️', '📦', '💰', '⭐', '✨'].map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handleAddEmoji(emoji)}
                        className="text-xl p-2 rounded-xl hover:bg-[#202c33] transition cursor-pointer"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}

                {/* Attachment Menu Popup */}
                {showAttachMenu && (
                  <div className="absolute bottom-16 left-12 bg-[#111b21] border border-[#2a3942] rounded-2xl p-2 shadow-2xl space-y-1 w-48 z-50">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAttachMenu(false);
                        setReplyMessage((prev) => prev + ' [Attached Catalog Item] ');
                      }}
                      className="w-full px-3 py-2 rounded-xl hover:bg-[#202c33] text-left text-xs text-[#d1d7db] flex items-center gap-2.5 transition cursor-pointer"
                    >
                      <ShoppingBag size={14} className="text-[#00a884]" />
                      <span>Product Catalog</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAttachMenu(false);
                        setReplyMessage((prev) => prev + ' Thank you for contacting us! How may I assist you today? ');
                      }}
                      className="w-full px-3 py-2 rounded-xl hover:bg-[#202c33] text-left text-xs text-[#d1d7db] flex items-center gap-2.5 transition cursor-pointer"
                    >
                      <Sparkles size={14} className="text-amber-400" />
                      <span>Quick Greeting</span>
                    </button>
                  </div>
                )}

                <form onSubmit={handleSendReply} className="flex items-center gap-2">
                  {/* Emoji Button */}
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-2 text-[#8696a0] hover:text-[#d1d7db] rounded-full hover:bg-[#374248] transition cursor-pointer"
                  >
                    <Smile size={20} />
                  </button>

                  {/* Attachment Button */}
                  <button
                    type="button"
                    onClick={() => setShowAttachMenu(!showAttachMenu)}
                    className="p-2 text-[#8696a0] hover:text-[#d1d7db] rounded-full hover:bg-[#374248] transition cursor-pointer"
                  >
                    <Paperclip size={20} />
                  </button>

                  {/* Message Input Pill */}
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="w-full bg-[#2a3942] rounded-xl px-4 py-2.5 text-sm text-[#d1d7db] placeholder-[#8696a0] focus:outline-none focus:ring-1 focus:ring-[#00a884]"
                    />
                  </div>

                  {/* Send / Mic Button */}
                  {replyMessage.trim() ? (
                    <button
                      type="submit"
                      disabled={sending}
                      className="w-10 h-10 rounded-full bg-[#00a884] hover:bg-[#00a884]/90 disabled:opacity-50 text-[#111b21] flex items-center justify-center transition cursor-pointer shrink-0 shadow-lg"
                      title="Send message via WhatsApp Cloud API"
                    >
                      <Send size={16} className="ml-0.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setReplyMessage('Hello! How can I assist you today?');
                      }}
                      className="p-2 text-[#8696a0] hover:text-[#d1d7db] rounded-full hover:bg-[#374248] transition cursor-pointer"
                      title="Insert Quick Reply"
                    >
                      <Mic size={20} />
                    </button>
                  )}
                </form>
              </div>
            </>
          ) : (
            /* Empty State: Select a Chat */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-[#111b21]/90">
              <div className="w-20 h-20 rounded-full bg-[#202c33] flex items-center justify-center text-[#00a884] mb-4 shadow-xl border border-[#2a3942]">
                <Smartphone size={36} />
              </div>
              <h3 className="text-lg font-bold text-[#e9edef] mb-1">
                Kangavena WhatsApp Web Console
              </h3>
              <p className="text-xs text-[#8696a0] max-w-sm leading-relaxed mb-4">
                Send and receive messages directly via Official Meta Cloud API. Select a customer conversation from the list to view real-time chat or take over.
              </p>
              <div className="flex items-center gap-2 text-[11px] text-[#00a884] font-mono bg-[#202c33] px-4 py-2 rounded-xl border border-[#2a3942]">
                <Lock size={12} />
                <span>End-to-End Sovereign Business Integration</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Direct Access & QR Modal */}
      <KangavenaWhatsAppMobileModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        token={token}
        tenant={tenant}
        connectedInstances={connectedInstances}
      />

      {/* Floating Action / Deletion Toast */}
      {deleteToast && (
        <div className="fixed bottom-5 right-5 z-50 px-4 py-3 rounded-xl bg-slate-900/95 border border-emerald-500/50 text-emerald-300 text-xs font-semibold shadow-2xl flex items-center gap-2.5 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <span>{deleteToast}</span>
        </div>
      )}

      {/* ── Single Conversation Delete Confirmation Modal ── */}
      {deleteConfirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-md bg-[#202c33] border border-[#2a3942] rounded-2xl shadow-2xl p-5 space-y-4 text-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-rose-400 shrink-0">
                <Trash2 size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-white">Delete Conversation?</h4>
                <p className="text-xs text-slate-400 truncate">
                  {deleteConfirmTarget.name} {deleteConfirmTarget.phone ? `(${formatPhoneNumber(deleteConfirmTarget.phone)})` : ''}
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed bg-[#111b21] p-3 rounded-xl border border-white/5">
              ⚠️ Are you sure you want to delete this chat history? This will permanently remove all stored messages for this contact from the database and wipe Luna AI&apos;s memory context.
            </p>

            <div className="flex items-center justify-end gap-2.5 pt-1">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setDeleteConfirmTarget(null)}
                className="px-4 py-2 rounded-xl bg-[#111b21] hover:bg-[#2a3942] text-xs font-semibold text-slate-300 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => handleDeleteChat(deleteConfirmTarget.sessionId)}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 active:scale-95 text-xs font-bold text-white transition flex items-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/30 disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw size={13} className="animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={13} />
                    <span>Delete Chat</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Clear All WhatsApp Chats Confirmation Modal ── */}
      {showClearAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-lg bg-[#202c33] border border-rose-500/40 rounded-2xl shadow-2xl p-6 space-y-4 text-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/20 border border-rose-500/50 flex items-center justify-center text-rose-400 shrink-0 shadow-inner">
                <Trash2 size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-base font-bold text-white">Clear All WhatsApp Chats?</h4>
                <p className="text-xs text-rose-300/90 font-mono">
                  Total Active Conversations: {sessions.length}
                </p>
              </div>
            </div>

            <div className="bg-rose-950/30 border border-rose-500/30 rounded-xl p-3.5 space-y-2 text-xs text-rose-200">
              <p className="font-bold flex items-center gap-1 text-rose-300">
                <span>⚠️ Danger Zone: Permanent Data Deletion</span>
              </p>
              <p className="text-slate-300 leading-relaxed">
                This action will permanently delete <strong>all WhatsApp conversation logs</strong> from the database and completely purge Luna AI&apos;s active in-memory context. Customer lead records and orders will remain safely intact in their respective modules.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setShowClearAllModal(false)}
                className="px-4 py-2 rounded-xl bg-[#111b21] hover:bg-[#2a3942] text-xs font-semibold text-slate-300 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleClearAllChats}
                className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 active:scale-95 text-xs font-black text-white transition flex items-center gap-2 cursor-pointer shadow-xl shadow-rose-600/30 disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    <span>Purging Chats...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    <span>Yes, Delete All WhatsApp Chats</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
